import { useEffect, useState } from 'react';
import { Alert, Box, Button, Sheet, Typography } from '@mui/joy';
import { api } from '../../lib/api';
import type { WorkflowExecution } from '../../types/workflows';
import WorkflowExecutionList from './WorkflowExecutionList';
import WorkflowExecutionDetail from './WorkflowExecutionDetail';

export default function AutomationRunHistory({ automationId }: { automationId: number }) {
  const [runs, setRuns] = useState<WorkflowExecution[]>([]);
  const [selected, setSelected] = useState<WorkflowExecution | null>(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [version, setVersion] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get<{ results: WorkflowExecution[]; next: string | null }>(`/api/automations/workflow-executions/?automation=${automationId}&page=${page}`)
      .then(response => { if (!cancelled) { setRuns(response.data.results); setHasNext(!!response.data.next); setError(''); } })
      .catch(() => { if (!cancelled) setError('Unable to load run history. Please retry.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [automationId, page, version]);
  const openRun = async (run: WorkflowExecution) => {
    try { setSelected((await api.get<WorkflowExecution>(`/api/automations/workflow-executions/${run.id}/`)).data); }
    catch { setError('Unable to load run details. Please retry.'); }
  };
  return <Sheet variant="outlined" sx={{ p: 2 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
      <Typography level="title-lg">Run history</Typography>
      <Button variant="outlined" loading={loading} onClick={() => { setVersion(value => value + 1); if (selected) void openRun(selected); }}>Refresh history</Button>
    </Box>
    {error && <Alert color="danger">{error}</Alert>}
    <WorkflowExecutionList executions={runs} onViewDetails={openRun} />
    <Box sx={{ display: 'flex', gap: 1, my: 2 }}>
      <Button variant="plain" disabled={page === 1 || loading} onClick={() => setPage(value => value - 1)}>Previous</Button>
      <Typography>Page {page}</Typography>
      <Button variant="plain" disabled={!hasNext || loading} onClick={() => setPage(value => value + 1)}>Next</Button>
    </Box>
    {selected && <>
      <WorkflowExecutionDetail execution={selected} onUserDecision={async (failureId, decision) => {
        try {
          await api.post(`/api/automations/workflow-failures/${failureId}/user-decision/`, { decision });
          await openRun(selected); setVersion(value => value + 1);
        } catch (err) { setError('Unable to update this run. Refresh to check its current status.'); throw err; }
      }} />
      {selected.status === 'waiting' && <Button color="danger" variant="outlined" onClick={async () => {
        try { await api.post(`/api/automations/workflow-executions/${selected.id}/cancel/`, {}); await openRun(selected); setVersion(value => value + 1); }
        catch { setError('Unable to cancel this run. Please refresh and retry.'); }
      }}>Cancel waiting run</Button>}
    </>}
  </Sheet>;
}
